import React from 'react';

import { Form } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import { normalizeValue } from 'utils/helpers/form';

import ShopDataModalFeesEntryNumeric from './ShopDataModalFeesEntryNumeric';
import { useContractContext } from '../../../common/Contract/ContractContext';
import Checkbox from '../../lib/Checkbox';
import InputNumber from '../../lib/InputNumber';
import ShopDataModalAddEntryArrow from '../common/ShopDataModalAddEntryArrow';
import ShopDataModalCategoryHeader from '../common/ShopDataModalCategoryHeader';
import {
  DEFAULT_FEES_FIELDS,
  FEES_FIELD_COMMUNITY_FEE,
  FEES_FIELD_COMMUNITY_FEE_EQUIVALENT_ENABLED,
  FEES_FIELD_FREQUENCY,
  FEES_FIELD_FREQUENCY_PERCENTAGE,
  FEES_FIELDS,
  FEES_FIELDS_KEYS,
  FEES_FIELDS_LABELS,
  FIELD_CATEGORY,
} from '../constants';
import { useShopDataModalContext } from '../ShopDataModalContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 20px 30px 20px;
`;

const AppendixElements = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;

const AppendixInputs = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  &.disabled {
    span {
      opacity: 0.5;
    }
  }
`;

const CommunityFeeAppendix = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const CustomElement = styled.div`
  display: flex;
  width: 205px;
  justify-content: end;
`;

const Trail = styled.div`
  width: 30px;
  height: 20px;
  border-left: 1px dashed var(--text-primary);
  border-bottom: 1px dashed var(--text-primary);
`;

const CheckboxLabel = styled.span`
  font-family: var(--font-bold);
  color: var(--text-primary);
  font-size: 14px;
`;

interface Props {
  onChange: () => void;
}

const ShopDataModalFeesCommunityFees = ({ onChange }: Props) => {

  const { disabled, currentLivingType, isCommunityCcrc, handleDataStatusChanged, currentData, setCurrentData } =
    useShopDataModalContext();
  const { selectedKey } = useContractContext();

  const handleAddFeeType = (item: string) => {
    if (!selectedKey) {
      return;
    }

    const livingTypeKey = currentLivingType?.abv;
    setCurrentData((prevState: any) => ({
      ...prevState,
      [FIELD_CATEGORY.FEES_INCENTIVES]: {
        ...prevState[FIELD_CATEGORY.FEES_INCENTIVES],
        [livingTypeKey]: {
          ...(prevState[FIELD_CATEGORY.FEES_INCENTIVES]?.[livingTypeKey] || []),
          [selectedKey as string]: {
            ...(prevState[FIELD_CATEGORY.FEES_INCENTIVES]?.[livingTypeKey]?.[selectedKey] || []),
            living_type_id: currentLivingType?.id,
            fields: [
              ...(prevState[FIELD_CATEGORY.FEES_INCENTIVES]?.[livingTypeKey]?.[selectedKey]?.['fields'] || []),
              item,
            ],
          },
        },
      },
    }));
    handleDataStatusChanged([FIELD_CATEGORY.FEES_INCENTIVES]);
    onChange();
  };

  const handleRemoveFeeType = (item: string) => {
    handleDataStatusChanged([FIELD_CATEGORY.FEES_INCENTIVES]);

    const removalFields =
      item === FEES_FIELD_COMMUNITY_FEE
        ? [
          item,
          FEES_FIELD_FREQUENCY,
          FEES_FIELD_COMMUNITY_FEE_EQUIVALENT_ENABLED,
          FEES_FIELD_FREQUENCY_PERCENTAGE,
        ]
        : [item];

    const livingTypeKey = currentLivingType?.abv;

    setCurrentData((prevState: any) => {
      const prevLivingType =
        prevState[FIELD_CATEGORY.FEES_INCENTIVES]?.[livingTypeKey] ?? {};

      const prevSelected =
        prevLivingType?.[selectedKey as string] ?? {};

      return {
        ...prevState,
        [FIELD_CATEGORY.FEES_INCENTIVES]: {
          ...prevState[FIELD_CATEGORY.FEES_INCENTIVES],
          [livingTypeKey]: {
            ...prevLivingType,
            [selectedKey as string]: {
              ...prevSelected,
              ...Object.fromEntries(removalFields.map((field) => [field, null])),
              fields: (prevSelected.fields ?? []).filter(
                (field: string) => !removalFields.includes(field)
              ),
            },
          },
        },
      };
    });

    onChange();
  };


  return (
    <>
      <ShopDataModalCategoryHeader label={'Community Fees'} />
      <Container>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {

            const populatedFields =
              getFieldValue([FIELD_CATEGORY.FEES_INCENTIVES, 'fields']) ?? [];

            const nonDefaultPopulatedFields = populatedFields.filter(
              (field: string) => !DEFAULT_FEES_FIELDS.includes(field)
            );

            const unpopulatedFields = FEES_FIELDS_KEYS.filter(
              (item: string) =>
                !populatedFields.includes(item) &&
                !DEFAULT_FEES_FIELDS.includes(item)
            );

            const frequencyFieldRequired = getFieldValue([
              FIELD_CATEGORY.FEES_INCENTIVES,
              FEES_FIELD_COMMUNITY_FEE_EQUIVALENT_ENABLED,
            ]);

            let fieldsOptions = unpopulatedFields.map((item: string) => ({
              key: item,
              label: FEES_FIELDS_LABELS[item],
              onClick: () => handleAddFeeType(item),
            }));

            if (!isCommunityCcrc) {
              const ccrcFees = [FEES_FIELDS.SECOND_PERSON_ENTRANCE_FEE];
              fieldsOptions = fieldsOptions.filter(
                (option) => !ccrcFees.includes(option.key)
              );
            }

            return (
              <>
                <Form.Item noStyle name={[FIELD_CATEGORY.FEES_INCENTIVES, 'id']} hidden />
                <Form.Item
                  noStyle
                  name={[FIELD_CATEGORY.FEES_INCENTIVES, 'living_type_id']}
                  hidden
                />

                {!isCommunityCcrc && (
                  <>
                    <ShopDataModalFeesEntryNumeric
                      disabled={frequencyFieldRequired || disabled}
                      key={FEES_FIELD_COMMUNITY_FEE}
                      category={FIELD_CATEGORY.FEES_INCENTIVES}
                      field={FEES_FIELD_COMMUNITY_FEE}
                      onRemove={handleRemoveFeeType}
                      dataTestId="sd-modal-community-fee-input"
                    />

                    <CommunityFeeAppendix>
                      <CustomElement>
                        <Trail />
                      </CustomElement>

                      <AppendixElements>
                        <Form.Item
                          name={[
                            FIELD_CATEGORY.FEES_INCENTIVES,
                            FEES_FIELD_COMMUNITY_FEE_EQUIVALENT_ENABLED,
                          ]}
                          valuePropName="checked"
                        >
                          <Checkbox className="green">
                            <CheckboxLabel>Equivalent to</CheckboxLabel>
                          </Checkbox>
                        </Form.Item>

                        <AppendixInputs
                          className={classNames({ disabled: !frequencyFieldRequired })}
                        >
                          <Form.Item
                            noStyle
                            name={[
                              FIELD_CATEGORY.FEES_INCENTIVES,
                              FEES_FIELD_FREQUENCY_PERCENTAGE,
                            ]}
                            rules={[
                              { required: frequencyFieldRequired },
                              { type: 'number', min: 0 },
                            ]}
                            normalize={normalizeValue({ decimals: 0 })}
                          >
                            <InputNumber
                              style={{ height: 36 }}
                              suffix="%"
                              disabled={!frequencyFieldRequired || disabled}
                              data-test-id="sd-modal-community-fee-percentage-input"
                            />
                          </Form.Item>
                          <span>of Monthly Fee</span>
                        </AppendixInputs>
                      </AppendixElements>
                    </CommunityFeeAppendix>
                  </>
                )}

                {DEFAULT_FEES_FIELDS.map((field) => (
                  <ShopDataModalFeesEntryNumeric
                    key={field}
                    category={FIELD_CATEGORY.FEES_INCENTIVES}
                    field={field}
                    onRemove={handleRemoveFeeType}
                    disabled={disabled}
                    dataTestId={`sd-modal-${field}-fee-input`}
                  />
                ))}

                {nonDefaultPopulatedFields.map((field: string) => (
                  <ShopDataModalFeesEntryNumeric
                    key={field}
                    category={FIELD_CATEGORY.FEES_INCENTIVES}
                    field={field}
                    onRemove={handleRemoveFeeType}
                    disabled={disabled}
                    required
                    dataTestId={`sd-modal-${field}-fee-input`}
                  />
                ))}

                {!!fieldsOptions.length && (
                  <ShopDataModalAddEntryArrow
                    label="Add Fee Type"
                    options={fieldsOptions}
                    dataTestId="sd-modal-fee-type-btn"
                  />
                )}
              </>
            );
          }}
        </Form.Item>
      </Container>
    </>
  );
};

export default ShopDataModalFeesCommunityFees;
